import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { API_BASE } from "@/App";
import { 
  ArrowRight, ChevronDown, ChevronUp, Settings, Award, Crown,
  Target, PenTool, AlertTriangle, Shield, Clock, Play,
  Brain, Newspaper, Sparkles, TrendingUp, Calendar, Gift,
  Flame, Zap, BookOpen, BarChart3, Trophy, Star, Rocket,
  CheckCircle, Timer, ChevronRight, Bell, Rss, ArrowLeft,
  GraduationCap, FileText, Lightbulb, TrendingDown, Circle, HelpCircle,
  MapPin, Landmark, Users, Building2
} from "lucide-react";

/**
 * TGPSC Dashboard - Telangana Group-1 Command Center
 * Reuses UPSC Dashboard structure with TGPSC-specific content
 * Features: Selection Probability, Streak, Daily Targets, Risk Meters
 */

const TGPSCDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [commandStatus, setCommandStatus] = useState(null);
  const [intelligenceReport, setIntelligenceReport] = useState(null);
  const [exploreExpanded, setExploreExpanded] = useState(false);
  const [streakData, setStreakData] = useState({ current_streak: 0, longest_streak: 0 });
  const [dailyTargets, setDailyTargets] = useState({
    mcqs: { target: 10, completed: 0 },
    answers: { target: 1, completed: 0 },
    ca: { target: 1, completed: 0 }
  });

  const isPro = user?.role === "PRO";
  const firstName = user?.name?.split(" ")[0] || "Aspirant";
  
  // TGPSC-specific quotes
  const quotes = [
    { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
    { text: "Telangana's history teaches us resilience and determination.", author: "CLARIAS TGPSC" },
    { text: "From Kakatiya to Kaleshwaram - know your state, ace the exam.", author: "TGPSC Wisdom" },
    { text: "One correct decision per day beats 10 random tasks.", author: "CLARIAS" },
    { text: "State-specific preparation is the key to Group-1 success.", author: "TGPSC Strategy" }
  ];
  const [currentQuote] = useState(quotes[Math.floor(Math.random() * quotes.length)]);
  
  // Time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: "Good morning", emoji: "🌅" };
    if (hour < 17) return { text: "Good afternoon", emoji: "☀️" };
    return { text: "Good evening", emoji: "🌙" };
  };
  const greeting = getGreeting();

  // Days to TGPSC Prelims 2026 (July 15)
  const TGPSC_DATE = new Date('2026-07-15');
  const getDaysToExam = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exam = new Date(TGPSC_DATE);
    exam.setHours(0, 0, 0, 0);
    return Math.ceil((exam - today) / (1000 * 60 * 60 * 24));
  };
  const daysToExam = getDaysToExam();
  const weeksToExam = Math.floor(daysToExam / 7);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    
    const fetchAllData = async () => {
      const token = localStorage.getItem("token");
      
      try {
        // Fetch dashboard data with exam_type=TGPSC
        const [dashRes, prepOsRes] = await Promise.all([
          fetch(`${API_BASE}/command/dashboard-all?exam_type=TGPSC`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          }),
          fetch(`${API_BASE}/prep-os/dashboard-stats`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          }).catch(() => null)
        ]);
        
        if (!dashRes.ok) throw new Error("Failed to fetch dashboard data");
        const data = await dashRes.json();
        
        let prepOsData = null;
        if (prepOsRes && prepOsRes.ok) {
          prepOsData = await prepOsRes.json();
        }
        
        if (isMounted) {
          if (data.command_status) setCommandStatus(data.command_status);
          if (data.intelligence_report) setIntelligenceReport(data.intelligence_report);
          
          // Streak Data
          if (prepOsData?.metrics?.streak_days !== undefined) {
            setStreakData({ 
              current_streak: prepOsData.metrics.streak_days, 
              longest_streak: prepOsData.metrics.streak_days 
            });
          } else if (data.streak) {
            setStreakData(data.streak);
          }
          
          // Daily Targets
          if (data.today_targets?.completion_details) {
            const details = data.today_targets.completion_details;
            setDailyTargets({
              mcqs: { target: 10, completed: details.mcqs_completed || 0 },
              answers: { target: 1, completed: details.mains_attempted ? 1 : 0 },
              ca: { target: 1, completed: details.ca_reviewed ? 1 : 0 }
            });
          }
        }
      } catch (err) {
        if (!isCancelError(err)) {
          console.error("Dashboard fetch error:", err);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAllData();
    
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "safe": return { bg: "from-emerald-500 to-green-500", text: "text-emerald-600", border: "border-emerald-400" };
      case "borderline": return { bg: "from-amber-500 to-orange-500", text: "text-amber-600", border: "border-amber-400" };
      case "unsafe": return { bg: "from-red-500 to-rose-500", text: "text-red-600", border: "border-red-400" };
      default: return { bg: "from-slate-400 to-slate-500", text: "text-slate-600", border: "border-slate-300" };
    }
  };

  // Quick stats
  const stats = {
    questionsAttempted: intelligenceReport?.weekly_update?.questions_this_week || 0,
    accuracy: intelligenceReport?.selection_probability?.components?.test_accuracy || intelligenceReport?.weekly_update?.current_accuracy || 0,
    accuracyTrend: intelligenceReport?.weekly_update?.accuracy_change || 0,
    streak: streakData.current_streak || 0,
    longestStreak: streakData.longest_streak || 0
  };

  // TGPSC Explore features
  const exploreFeatures = [
    { title: "Prelims Practice", icon: Target, path: "/tgpsc/prelims", color: "bg-gradient-to-br from-pink-500 to-rose-500", desc: "MCQ Practice" },
    { title: "PYQ Genome", icon: BarChart3, path: "/tgpsc/genome", color: "bg-gradient-to-br from-purple-500 to-indigo-500", desc: "Topic Rotation" },
    { title: "Topic Heatmap", icon: Flame, path: "/tgpsc/heatmap", color: "bg-gradient-to-br from-orange-500 to-red-500", desc: "Priority Topics" },
    { title: "MCQ Practice", icon: Target, path: "/tgpsc/mcq", color: "bg-gradient-to-br from-emerald-500 to-teal-500", desc: "AI Questions" },
    { title: "Mains Hub", icon: PenTool, path: "/tgpsc/mains", color: "bg-gradient-to-br from-indigo-500 to-blue-500", desc: "Answer Writing" },
    { title: "News Hub", icon: Newspaper, path: "/tgpsc/news", color: "bg-gradient-to-br from-amber-500 to-orange-500", desc: "Current Affairs" },
    { title: "Editorial Analyzer", icon: FileText, path: "/tgpsc/editorial", color: "bg-gradient-to-br from-cyan-500 to-blue-500", desc: "Analyze Articles" },
    { title: "Analytics", icon: BarChart3, path: "/tgpsc/analytics", color: "bg-gradient-to-br from-violet-500 to-purple-500", desc: "Performance" },
  ];

  // TGPSC-specific subjects
  const tgpscSubjects = [
    { title: "Telangana History", icon: Landmark, path: "/tgpsc/mcq?topic=Telangana+History", color: "from-pink-500 to-rose-600" },
    { title: "Telangana Geography", icon: MapPin, path: "/tgpsc/mcq?topic=Telangana+Geography", color: "from-emerald-500 to-teal-600" },
    { title: "Telangana Movement", icon: Users, path: "/tgpsc/mcq?topic=Telangana+Movement", color: "from-orange-500 to-amber-600" },
    { title: "Telangana Economy", icon: Building2, path: "/tgpsc/mcq?topic=Telangana+Economy", color: "from-blue-500 to-indigo-600" },
  ];

  // Focus topic for TGPSC
  const getFocusTopic = () => {
    if (commandStatus?.gs_status === "unsafe" || commandStatus?.gs_status === "borderline") {
      return { topic: "Telangana History - Kakatiya Dynasty", reason: "Strengthen state-specific knowledge", path: "/tgpsc/mcq?topic=Kakatiya+Dynasty", urgency: "high" };
    }
    return { topic: "Current Affairs - Telangana", reason: "Stay updated with state developments", path: "/tgpsc/news", urgency: "normal" };
  };
  const focusTopic = getFocusTopic();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-pink-950 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <Rocket className="w-6 h-6 text-pink-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-pink-300 font-medium">Loading TGPSC Command Center...</p>
        </div>
      </div>
    );
  }

  const selectionProb = intelligenceReport?.selection_probability?.probability || 35;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-pink-50/30 to-rose-50/30" data-testid="tgpsc-dashboard">
      {/* Back to Exam Selector */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/exam-select")}
          className="text-slate-600 hover:text-slate-900"
          data-testid="back-to-exam-select"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Switch Exam
        </Button>
      </div>

      {/* VIBRANT HEADER - Pink Theme for TGPSC */}
      <div className="bg-gradient-to-r from-pink-600 via-rose-600 to-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          {/* Top Row */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-pink-200 text-sm flex items-center gap-1">
                {greeting.emoji} {greeting.text}, <span className="font-medium text-white">{firstName}</span>
              </p>
              <div className="flex items-center gap-2 mt-1">
                <h1 className="text-2xl font-bold font-heading">TGPSC Group-1</h1>
                {isPro && (
                  <Badge className="bg-gradient-to-r from-amber-400 to-orange-400 text-white border-0 font-semibold px-2 py-0.5">
                    <Crown className="w-3 h-3 mr-1" />
                    PRO
                  </Badge>
                )}
              </div>
              <p className="text-pink-200 text-xs mt-1">Telangana Public Service Commission</p>
            </div>
            
            {/* Days Counter & Settings */}
            <div className="flex items-center gap-3">
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="text-2xl font-bold leading-none">{daysToExam}</p>
                <p className="text-[10px] text-pink-200">days to Prelims</p>
              </div>
              <button
                onClick={() => navigate("/account-settings")}
                className="p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Streak & Probability Row */}
          <div className="grid grid-cols-3 gap-3">
            {/* Selection Probability */}
            <div 
              className="bg-white/10 backdrop-blur-sm rounded-xl p-3 cursor-pointer hover:bg-white/20 transition-all"
              onClick={() => navigate("/tgpsc/analytics")}
            >
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-300" />
                <span className="text-xs text-pink-200">Selection Prob.</span>
              </div>
              <p className="text-xl font-bold">{selectionProb.toFixed(1)}%</p>
            </div>

            {/* Streak */}
            <div className="bg-gradient-to-br from-orange-500/30 to-red-500/30 backdrop-blur-sm rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <Flame className="w-4 h-4 text-orange-300 animate-pulse" />
                <span className="text-xs text-orange-200">Streak</span>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-xl font-bold">{stats.streak}</p>
                <span className="text-xs text-orange-200">days</span>
                {stats.streak >= 3 && <span className="text-lg">🔥</span>}
              </div>
            </div>

            {/* Accuracy */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-cyan-300" />
                <span className="text-xs text-pink-200">Accuracy</span>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-xl font-bold">{stats.accuracy}%</p>
                {stats.accuracyTrend > 0 ? (
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                ) : stats.accuracyTrend < 0 ? (
                  <TrendingDown className="w-4 h-4 text-red-400" />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        
        {/* Upgrade Banner for FREE users */}
        {!isPro && (
          <Card 
            className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 overflow-hidden cursor-pointer hover:shadow-lg transition-all"
            onClick={() => navigate("/upgrade")}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-lg text-slate-800">Upgrade to PRO</p>
                    <Badge className="bg-green-500 text-white border-0 text-xs font-bold">
                      83% OFF
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600">Unlimited MCQs, AI evaluation, full PYQ analysis & more</p>
                </div>
                <Button 
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 font-semibold"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Upgrade
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Motivational Quote */}
        <div className="bg-gradient-to-r from-pink-100 to-rose-100 rounded-2xl p-4 border border-pink-200/50">
          <p className="text-sm text-pink-800 italic">"{currentQuote.text}"</p>
          <p className="text-xs text-pink-600 mt-1">— {currentQuote.author}</p>
        </div>

        {/* Today's Learning Targets */}
        <Card className="border-0 shadow-lg bg-white overflow-hidden">
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Target className="w-4 h-4" />
                <span className="font-semibold text-sm">Today's Learning Targets</span>
              </div>
              <Badge className="bg-white/20 text-white border-0 text-xs">
                {dailyTargets.mcqs.completed + dailyTargets.answers.completed + dailyTargets.ca.completed}/
                {dailyTargets.mcqs.target + dailyTargets.answers.target + dailyTargets.ca.target} done
              </Badge>
            </div>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4">
              {/* MCQs Target */}
              <div 
                className="text-center p-3 bg-pink-50 rounded-xl cursor-pointer hover:bg-pink-100 transition-colors"
                onClick={() => navigate("/tgpsc/mcq")}
              >
                <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold text-pink-600">{dailyTargets.mcqs.completed}/{dailyTargets.mcqs.target}</p>
                <p className="text-xs text-slate-600">MCQs</p>
                <Progress value={(dailyTargets.mcqs.completed / dailyTargets.mcqs.target) * 100} className="h-1.5 mt-2" />
              </div>

              {/* Answers Target */}
              <div 
                className="text-center p-3 bg-purple-50 rounded-xl cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => navigate("/tgpsc/mains")}
              >
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <PenTool className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold text-purple-600">{dailyTargets.answers.completed}/{dailyTargets.answers.target}</p>
                <p className="text-xs text-slate-600">Answers</p>
                <Progress value={(dailyTargets.answers.completed / dailyTargets.answers.target) * 100} className="h-1.5 mt-2" />
              </div>

              {/* CA Target */}
              <div 
                className="text-center p-3 bg-blue-50 rounded-xl cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => navigate("/tgpsc/news")}
              >
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Newspaper className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold text-blue-600">{dailyTargets.ca.completed}/{dailyTargets.ca.target}</p>
                <p className="text-xs text-slate-600">CA Review</p>
                <Progress value={(dailyTargets.ca.completed / dailyTargets.ca.target) * 100} className="h-1.5 mt-2" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Primary Hubs */}
        <div className="grid grid-cols-2 gap-4">
          {/* Prelims Hub */}
          <Card 
            className="border-0 shadow-lg bg-gradient-to-br from-pink-500 to-rose-600 text-white cursor-pointer hover:shadow-xl transition-all hover:scale-[1.02]"
            onClick={() => navigate("/tgpsc/prelims")}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6" />
                </div>
                <Badge className="bg-white/20 border-0">Paper I & II</Badge>
              </div>
              <h3 className="font-bold text-lg mb-1">Prelims Hub</h3>
              <p className="text-pink-100 text-sm mb-3">MCQ Practice & PYQ Analysis</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-pink-200">{daysToExam} days left</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Mains Hub */}
          <Card 
            className="border-0 shadow-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white cursor-pointer hover:shadow-xl transition-all hover:scale-[1.02]"
            onClick={() => navigate("/tgpsc/mains")}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <PenTool className="w-6 h-6" />
                </div>
                <Badge className="bg-white/20 border-0">GS I-IV + Essay</Badge>
              </div>
              <h3 className="font-bold text-lg mb-1">Mains Hub</h3>
              <p className="text-indigo-100 text-sm mb-3">Answer Writing & Evaluation</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-indigo-200">Start practicing</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Telangana-Specific Subjects */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-2.5">
            <div className="flex items-center gap-2 text-white">
              <Landmark className="w-4 h-4" />
              <span className="font-semibold text-sm">Telangana-Specific Subjects</span>
              <Badge className="bg-white/20 border-0 text-xs ml-auto">State Focus</Badge>
            </div>
          </div>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {tgpscSubjects.map((subject) => (
                <div
                  key={subject.title}
                  className={`p-4 rounded-xl bg-gradient-to-br ${subject.color} text-white cursor-pointer hover:opacity-90 transition-all`}
                  onClick={() => navigate(subject.path)}
                >
                  <subject.icon className="w-6 h-6 mb-2" />
                  <p className="font-medium text-sm">{subject.title}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Focus Topic Recommendation */}
        <Card className={`border-2 ${focusTopic.urgency === 'high' ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${focusTopic.urgency === 'high' ? 'bg-red-500' : 'bg-emerald-500'}`}>
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800">{focusTopic.topic}</p>
                <p className="text-sm text-slate-600">{focusTopic.reason}</p>
              </div>
              <Button
                size="sm"
                className={focusTopic.urgency === 'high' ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'}
                onClick={() => navigate(focusTopic.path)}
              >
                Start Now
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Explore Features */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <button
              onClick={() => setExploreExpanded(!exploreExpanded)}
              className="flex items-center justify-between w-full"
            >
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-pink-500" />
                <span className="font-semibold text-slate-800">Explore All Features</span>
              </div>
              {exploreExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {exploreExpanded && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {exploreFeatures.map((feature) => (
                  <div
                    key={feature.title}
                    className={`${feature.color} p-4 rounded-xl text-white cursor-pointer hover:opacity-90 transition-all`}
                    onClick={() => navigate(feature.path)}
                  >
                    <feature.icon className="w-5 h-5 mb-2" />
                    <p className="font-medium text-sm">{feature.title}</p>
                    <p className="text-xs opacity-80">{feature.desc}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* TGPSC Info */}
        <Card className="bg-slate-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800">About TGPSC Group-1</h4>
                <p className="text-sm text-slate-600 mt-1">
                  The Telangana State Public Service Commission Group-1 examination consists of 
                  Prelims (300 marks), Mains (900 marks), and Interview (75 marks). 
                  Our platform covers both national topics and Telangana-specific content to help you succeed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TGPSCDashboardPage;
